import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { getSchedulingWindowsLOV, type SchedulingWindowSummary } from 'api/scheduling_windows/operations/getSchedulingWindowsLOV';

import { EditReference } from './EditReference';

interface Props {
  // The stored scheduling_window_id, or null for "no restriction".
  readonly value: number | null;
  readonly onChange: (value: number | null) => void;
  readonly label?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
}

const getKey = (w: SchedulingWindowSummary) => w.scheduling_window_id;
const getLabel = (w: SchedulingWindowSummary): string => w.name;

/**
 * Searchable dropdown for scheduling windows keyed on `scheduling_window_id` — the id-keyed sibling of
 * {@link EditSchedulingWindowReference} (which keys on `zone_name|name`), for callers that store the numeric id. The
 * id↔display-name expansion is internal: the caller works in plain ids, and the eager LOV resolves a stored id to its name
 * via `findByKey` (the window universe is small, so no server `resolve` is needed). Selection returns the id.
 */
export const EditSchedulingWindowIdReference = ({ value, onChange, label = 'Scheduling Window', mandatory, disabled }: Props) => {
  // Inflate the stored id to the partial summary EditReference selects on; the empty name defers label resolution to the
  // eager LOV's findByKey. Memoised on the id so the reference model isn't rebuilt each render.
  const selected = useMemo<SchedulingWindowSummary | null>(() => (value !== null ? { scheduling_window_id: value, name: '', zone_name: '' } : null), [value]);

  return (
    <EditReference<SchedulingWindowSummary>
      label={label}
      value={selected}
      onChange={(w) => onChange(w?.scheduling_window_id ?? null)}
      source={getSchedulingWindowsLOV}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(w, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{w.name}</Highlighter>
          </span>
          <span className="text-body-secondary">{w.zone_name}</span>
        </div>
      )}
    />
  );
};
